import { makeStyles } from '@mui/styles';
import React, { useState, useEffect  } from 'react';
import { FaDatabase, FaBrain, FaLaptopCode, FaChartBar, FaBullhorn, FaUsers } from 'react-icons/fa';
import { Navigate, useLocation, useNavigate  } from 'react-router-dom'; 
import OurNumbers from './Services/OurNumbers';

const servicesData = [
    {
        title: "Data Science & AI",
        category: "Software and AI Services",
        description: [
            "Predict and optimize business outcomes with ML models.",
            "Gain insights using deep learning and NLP.",
            "Develop AI-driven automation tools."
        ],
        logo: <FaBrain size={40} />,
        link: "/services/data-science-ai"
    },
    {
        title: "Data Engineering",
        category: "Software and AI Services",
        description: [
            "Build scalable data architectures.",
            "Design and optimize ETL pipelines.",
            "Implement real-time data solutions."
        ],
        logo: <FaDatabase size={40} />,
        link: "/services/data-engineering"
    },
    {
        title: "Code Masters",
        category: "Software and AI Services",
        description: [
            "Proficient in major programming languages.",
            "Expert in full-stack development.",
            "Deliver high-quality, scalable code."
        ],
        logo: <FaLaptopCode size={40} />,
        link: "/services/code-masters"
    },
    {
        title: "Business Intelligence",
        category: "Software and AI Services",
        description: [
            "Transform data into actionable insights.",
            "Create interactive dashboards.",
            "Empower teams with self-service BI."
        ],
        logo: <FaChartBar size={40} />,
        link: "/services/business-intelligence"
    },
    {
        title: "Digital Marketing",
        category: "Digital Marketing Services",
        description: [
            "Increase visibility with SEO and PPC.",
            "Create targeted campaigns.",
            "Build long-term customer engagement."
        ],
        logo: <FaBullhorn size={40} />,
        link: "/services/digital-marketing"
    },
    {
        title: "Talent Acquisition",
        category: "Talent Acquisition Services",
        description: [
            "Customized enterprise and individual solutions.",
            "Executive search, recruitment outsourcing, and more.",
            "End-to-end talent acquisition services."
        ],
        logo: <FaUsers size={40} />,
        link: "/services/talent-acquisition"
    }
];


const useStyles = makeStyles(() => ({
    container: {
        textAlign: "center",
        padding: "40px",
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
    },
    dropdown: {
        padding: "10px",
        fontSize: "1rem",
        borderRadius: "5px",
        border: "1px solid #ccc",
        marginBottom: "20px",
        cursor: "pointer",
    },
    gridContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "20px",
        justifyContent: "center",
    },
    card: {
        border: "1px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "10px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        backdropFilter: "blur(10px)",
        maxWidth: "350px",
    },
    logoBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "80px",
        height: "80px",
        borderRadius: "10px",
        backgroundColor: "rgba(200, 239, 137, 0.15)",
        marginBottom: "15px",
        boxShadow: "0 0 7px #C8EF89, 0 0 7px #C8EF89, 0 0 17px #C8EF89",
    },
    button: {
        marginTop: "15px",
        padding: "10px 15px",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        color: "#fff",
        border: "none",
        borderRadius: "5px",
        cursor: "pointer",
        fontWeight: "bold",
        fontSize: "1rem",
        boxShadow: "0 0 5px #fff, 0 0 10px #fff, 0 0 15px #fff",
    },
    bulletPoints: {
        listStyleType: "none",
        paddingLeft: "0",
        color: "#fff",
        fontSize: "1rem",
        lineHeight: "1.5",
    }
}));

const Services = () => {
    const navigate = useNavigate();
    const classes = useStyles();
    const location = useLocation();

    const [selectedCategory, setSelectedCategory] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        const categoryParam = searchParams.get('category');
        return categoryParam || "All"; 
    });


    useEffect(() => {  
        window.scrollTo(0, 0);
    }, [location.search]); 

    const filteredServices = selectedCategory === "All"
        ? servicesData
        : servicesData.filter(service => service.category === selectedCategory);

        return (
            <div className={classes.container}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#333', marginBottom: '10px' }}>
                    Unlock Your Digital Potential
                </h1>
                <p style={{ fontSize: '1.2rem', color: '#555', maxWidth: '800px', margin: '0 auto 30px', lineHeight: '1.5' }}>
                    Explore a range of cutting-edge, customized solutions—from Data Engineering to AI Excellence—designed to drive your digital growth and success.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '20px' }}>
                    <select 
                        className={classes.dropdown} 
                        onChange={(e) => setSelectedCategory(e.target.value)} 
                        value={selectedCategory} 
                        style={{ paddingRight: '30px', height: '48px' }}
                    >
                        <option value="All">All</option>
                        <option value="Software and AI Services">Software & AI Services</option>
                        <option value="Digital Marketing Services">Digital Marketing Services</option>
                        <option value="Talent Acquisition Services">Talent Acquisition Services</option>
                    </select>
                </div>
                <div className={classes.gridContainer}>
                    {filteredServices.map((service, index) => (
                        <div key={index} className={classes.card}>
                            <div className={classes.logoBox}>{service.logo}</div>
                            <h2 style={{ fontSize: '1.5rem', color: '#222' }}>{service.title}</h2>
                            <ul className={classes.bulletPoints}>
                                {service.description.map((point, idx) => (
                                    <li key={idx} style={{ marginBottom: "8px", color: 'black', fontSize: '0.9rem' }}>{point}</li>
                                ))}
                            </ul>
                            <button 
                                className={classes.button} 
                                onClick={() => navigate(service.link)}
                            >
                                Learn More
                            </button>
                        </div>
                    ))}
                </div>
                <OurNumbers/>
            </div>
        );
        
};

export default Services;
